import { AccountInterface } from "../interfaces";

/**
 * Completitud fiscal del TITULAR de un documento ("FACTURAR A").
 *
 * Vive aparte de `accountValidations.tsx` a propósito, por dos razones:
 *
 *  1. `validateAccount` se usa para remitente y destinatario (ShipmentFormList,
 *     ShipmentForm, InternalMail). Un destinatario legítimamente no tiene
 *     dirección fiscal; exigírsela rompería la creación de guías.
 *  2. `accountValidations.tsx` importa `loaderService` y `dnsLookup` de
 *     `../services`, y `documentServices.tsx` importa de `../utils`: ya existe un
 *     ciclo services → utils → services. Este módulo es hoja (sólo importa de
 *     `../interfaces`) para que la capa de servicios PUEDA usarlo sin
 *     profundizarlo. Por lo mismo NO se exporta desde `src/utils/index.tsx`:
 *     impórtalo directo.
 *
 * ESTADO: `hasFiscalAddress` y `missingFiscalAddressCopy` están en uso. En
 * cambio `validateBillingAccount` y `MISSING_FISCAL_ADDRESS_ERROR` aterrizan por
 * delante de su consumidor: la guarda de la capa de servicios (los tres
 * escritores de `AccountLegalAddress` en `documentServices.tsx`) es trabajo
 * pendiente, anotado en TODOS.md. Hoy NO existe ninguna guarda de servicios; la
 * protección es sólo de UI. No leas este archivo como si la hubiera.
 *
 * Base normativa: Providencia SENIAT sobre medios digitales para emisión de
 * facturas (17-dic-2025), Art. 7 numeral 7, que exige del adquiriente nombre o
 * razón social, RIF y domicilio fiscal.
 */

/**
 * Texto para el operador: título + cuerpo que nombra al cliente y termina en verbo.
 *
 * El fallback usa `||` y no `??` a propósito: `accountFullName` puede llegar como
 * cadena vacía (el mapeo de documentos hace un cast ciego del DTO y la interfaz no
 * garantiza el campo), y `??` la dejaría pasar, produciendo un cuerpo que empieza
 * con un espacio suelto.
 */
export const missingFiscalAddressCopy = (account?: AccountInterface) => ({
  title: "Falta la dirección fiscal",
  body:
    `${account?.accountFullName?.trim() || "El cliente a facturar"} no tiene ` +
    `dirección fiscal registrada. Agrégala para poder cobrar.`,
});

/** Texto corto para el `didError` de la capa de servicios. */
export const MISSING_FISCAL_ADDRESS_ERROR =
  "El cliente a facturar no tiene dirección fiscal registrada";

/**
 * Tope del campo de dirección fiscal.
 *
 * No sale del ancho de columna —que no está declarado en ninguna parte del
 * backend— sino del punto donde el dato deja de ser nuestro: ApiCore trunca la
 * dirección a 191 caracteres antes de mandarla al proveedor fiscal
 * (`DocumentRepository.GetDocumentToInvoice`). Más allá de eso el recorte ocurre
 * igual, pero en silencio y sobre la factura ya emitida. Cortar aquí lo pone
 * delante del operador, que es quien puede resumir la dirección.
 */
export const FISCAL_ADDRESS_MAX_LENGTH = 192;

/**
 * El `.trim()` es esencial: hoy `!!" "` es `true`, así que una dirección de sólo
 * espacios pasa las compuertas de render y llega al payload.
 */
export const hasFiscalAddress = (account?: AccountInterface) =>
  !!account?.fiscalAddress?.trim();

/** No aplica a remitente/destinatario — sólo al titular del documento. */
export const validateBillingAccount = (account?: AccountInterface) => {
  if (!account) {
    return { error: true, message: "Debe seleccionar un cliente a facturar" };
  }

  if (!hasFiscalAddress(account)) {
    return { error: true, message: MISSING_FISCAL_ADDRESS_ERROR };
  }

  return { error: false, message: "" };
};
